import { useCallback, useMemo, useRef } from "react";

import type { DragEndEvent } from "@dnd-kit/core";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import type {
    OverviewWidgetId,
    OverviewWidgetItem,
} from "app/pages/sales-intelligence/pages/management/constants/overviewWidgetsConfig";
import {
    FIXED_OVERVIEW_WIDGET_DEF,
    OVERVIEW_WIDGET_DEF_MAP,
    OVERVIEW_WIDGET_DEFS,
} from "app/pages/sales-intelligence/pages/management/constants/overviewWidgetsConfig";
import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";
import { AssetsService } from "app/services/AssetsService";

import { SettingsSectionTitle } from "../SettingsSectionTitle/SettingsSectionTitle";

import { SortableWidgetRow } from "./SortableWidgetRow";
import { StyledItalicText, TooltipImage } from "./styles";
import { WidgetRow } from "./WidgetRow";

type OverviewWidgetsSectionProps = {
    widgets: OverviewWidgetItem[];
    onReorder: (fromIndex: number, toIndex: number) => void;
    onToggle: (id: OverviewWidgetId) => void;
};

const POINTER_ACTIVATION_CONSTRAINT = { distance: 5 };

export const OverviewWidgetsSection = ({ widgets, onReorder, onToggle }: OverviewWidgetsSectionProps) => {
    const { translate } = useTranslate();

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: POINTER_ACTIVATION_CONSTRAINT }));

    const preloadedImages = useRef(new Set<string>());
    const preloadImage = useCallback((url: string) => {
        if (preloadedImages.current.has(url)) {
            return;
        }
        preloadedImages.current.add(url);
        const img = new Image();
        img.src = url;
    }, []);

    const widgetIds = useMemo(() => widgets.map((w) => w.id), [widgets]);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
                return;
            }
            const oldIndex = widgets.findIndex((w) => w.id === active.id);
            const newIndex = widgets.findIndex((w) => w.id === over.id);
            extensionManagementTrackingService.trackReorderWidgets();
            onReorder(oldIndex, newIndex);
        },
        [widgets, onReorder],
    );

    const handleToggleWidget = useCallback(
        (widget: OverviewWidgetItem) => {
            if (widget.isChecked) {
                extensionManagementTrackingService.trackRemoveWidget(widget.id, widgets);
            } else {
                extensionManagementTrackingService.trackAddWidget(widget.id, widgets);
            }
            onToggle(widget.id);
        },
        [widgets, onToggle],
    );

    const tooltipContentMap = useMemo(
        () =>
            new Map(
                OVERVIEW_WIDGET_DEFS.map((def) => [
                    def.id,
                    def.exampleType === "text" ? (
                        <Text
                            as="span"
                            fontVariant="sectionTitle"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(def.exampleContent)}
                        </Text>
                    ) : (
                        <Stack
                            gap="4px"
                            alignItems="center"
                        >
                            <TooltipImage
                                src={AssetsService.assetUrl(def.exampleContent)}
                                alt={translate(def.titleKey)}
                            />
                            <StyledItalicText
                                fontVariant="sectionTitle"
                                color={colorsPalettes.carbon["300"]}
                            >
                                {translate("si.management.extension.settings.widget.image.example")}
                            </StyledItalicText>
                        </Stack>
                    ),
                ]),
            ),
        [translate],
    );

    return (
        <Stack
            gap="8px"
            data-automation="sales-intelligence-extension-overview-widgets"
        >
            <SettingsSectionTitle
                title={translate("si.management.extension.settings.overview_widgets.title")}
                description={translate("si.management.extension.settings.overview_widgets.description")}
            />

            <Stack
                gap="4px"
                width="100%"
            >
                <WidgetRow
                    title={translate(FIXED_OVERVIEW_WIDGET_DEF.titleKey)}
                    description={translate(FIXED_OVERVIEW_WIDGET_DEF.descriptionKey)}
                    isChecked
                    isFixed
                    dataAutomation="sales-intelligence-extension-widget-overview"
                    onMouseEnter={() => preloadImage(AssetsService.assetUrl(FIXED_OVERVIEW_WIDGET_DEF.exampleContent))}
                    tooltipContent={
                        <Stack
                            gap="4px"
                            alignItems="center"
                        >
                            <TooltipImage
                                src={AssetsService.assetUrl(FIXED_OVERVIEW_WIDGET_DEF.exampleContent)}
                                alt={translate(FIXED_OVERVIEW_WIDGET_DEF.titleKey)}
                            />
                            <StyledItalicText
                                fontVariant="sectionTitle"
                                color={colorsPalettes.carbon["300"]}
                            >
                                {translate("si.management.extension.settings.widget.image.example")}
                            </StyledItalicText>
                        </Stack>
                    }
                />

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={widgetIds}
                        strategy={verticalListSortingStrategy}
                    >
                        {widgets.map((widget) => {
                            const def = OVERVIEW_WIDGET_DEF_MAP.get(widget.id);
                            if (!def) {
                                return null;
                            }
                            return (
                                <SortableWidgetRow
                                    key={widget.id}
                                    id={widget.id}
                                    title={translate(def.titleKey)}
                                    description={translate(def.descriptionKey)}
                                    tooltipContent={tooltipContentMap.get(widget.id)}
                                    isChecked={widget.isChecked}
                                    onToggle={() => handleToggleWidget(widget)}
                                    onMouseEnter={
                                        def.exampleType === "image"
                                            ? () => preloadImage(AssetsService.assetUrl(def.exampleContent))
                                            : undefined
                                    }
                                    dataAutomation={`sales-intelligence-extension-widget-${widget.id}`}
                                />
                            );
                        })}
                    </SortableContext>
                </DndContext>
            </Stack>
        </Stack>
    );
};
